import {
  Assessment,
  PatientPrimaryPolicyholderInput,
} from '../generated/graphql';
import { GuardianDemographicsState } from '../redux/types';

// TODO: Maybe the redux config should consume this.
export const codeToQuestion: Record<string, string> = {
  RV_MainReason: 'Main reason for visit?',
  RV_SecondaryReason: 'Secondary reason for visit?',
  RV_SpecialtyCare: 'Requires specialty care?',
  RV_DiagnosedADHD: 'Previously diagnosed with ADHD?',
  RV_PsychiatricImpatient: 'Recently in-patient?',
  RV_KillingYourself: 'Suicidal?',
  RV_DrinkingAlcohol: 'Alcohol use',
  RV_UseOfDrugs: 'Drug use',
  MI_InsuranceProvider: 'Insurance',
  MI_InsurancePlan: 'Plan',
  MI_InsuranceId: 'Insurance Member ID',
  MI_PrescriptionMedication: 'Currently taking medication?',
  MI_MedicationList: 'Current Medications',
  MI_OpenPsychiatricMeds: 'Open to psychiatric meds?',
  MI_HaveAppointment: 'Do you already have an initial appointment scheduled?',
  PR_Gender: 'Gender',
  PR_PrimaryLanguage: 'Languague',
  PR_Ethnicity: 'Ethnicity',
};

export const freeTextAnswerDisplay = (
  code,
  answerDisplay,
  answers: Assessment[]
) => {
  const FREE_TEXT = answers.find(
    ans => ans.questionCode === `${code}FreeText`
  )?.answerValue;

  return FREE_TEXT
    ? answerDisplay.replace("I'm not sure", `I'm not sure: ${FREE_TEXT}`)
    : answerDisplay;
};

export const toFlatText = (answers: Assessment[]) => {
  const questionOrder = Object.keys(codeToQuestion);
  const sortedAnswers = [...answers].sort((a, b) => {
    const index1 = questionOrder.indexOf(a.questionCode);
    const index2 = questionOrder.indexOf(b.questionCode);
    return (
      (index1 > -1 ? index1 : Infinity) - (index2 > -1 ? index2 : Infinity)
    );
  });

  return sortedAnswers
    .map(item => {
      const questionText = codeToQuestion[item.questionCode];

      // Note: Kinda awkward that we're looping over the answers
      // and, for each item, looping over them again
      // in `freeTextAnswerDisplay` to find the same item. 🤷‍♂️
      const answerDisplay = freeTextAnswerDisplay(
        item.questionCode,
        item.answerValue,
        answers
      );

      return questionText
        ? `${questionText} = ${answerDisplay.split('|')}`
        : null;
    })
    .join('\n');
};

export enum PolicyHolderRelationLabelValueMap {
  'Me' = 'Me',
  'Spouse' = 'Spouse',
  'Parent' = 'Parent',
  'Stepparent' = 'Stepparent',
  'Foster parent' = 'Foster parent',
  'Grandparent' = 'Grandparent',
  'Other' = 'Other',
}

export enum MinorPolicyHolderRelationLabelValueMap {
  'Primary guardian' = 'Primary guardian',
  'Secondary guardian' = 'Secondary guardian',
  'Other' = 'Other',
}

export enum GuardianRelationLabelValueMap {
  'Father' = 'Father',
  'Mother' = 'Mother',
  'Foster Father' = 'Foster Father',
  'Foster Mother' = 'Foster Mother',
  'Grandfather' = 'Grandfather',
  'Grandmother' = 'Grandmother',
  'Stepmother' = 'Stepmother',
  'Stepfather' = 'Stepfather',
  'Other' = 'Other',
}

export enum SexLabelValueMap {
  'Unknown' = 'Unknown',
  'Male' = 'Male',
  'Female' = 'Female',
}

export const AdultPolicyholderOptions = Object.keys(
  PolicyHolderRelationLabelValueMap
).map(label => ({
  label,
  value: PolicyHolderRelationLabelValueMap[label],
}));

export const minorPolicyholderOptions = (includeSecondaryGuardian: boolean) => {
  const options = [];

  Object.keys(MinorPolicyHolderRelationLabelValueMap).forEach(label => {
    if (
      label === 'Primary guardian' ||
      label === 'Other' ||
      includeSecondaryGuardian
    ) {
      options.push({
        label,
        value: MinorPolicyHolderRelationLabelValueMap[label],
      });
    }
  });

  return options;
};

export const createPolicyholderObjectHelper = (
  patientGuardians: GuardianDemographicsState,
  relationToPatient: string
) => {
  if (!patientGuardians) return undefined;

  const guardian =
    relationToPatient ===
    MinorPolicyHolderRelationLabelValueMap['Secondary guardian']
      ? 'secondary'
      : 'primary';

  const policyholderInfo: PatientPrimaryPolicyholderInput = {
    firstName: patientGuardians[guardian].firstName,
    lastName: patientGuardians[guardian].lastName,
    birthDate: patientGuardians[guardian].birthDate,
    relationToPatient,
    isAddressLinkedWithPatient:
      patientGuardians[guardian].isAddressLinkedWithPatient,
    ...(!patientGuardians[guardian]?.isAddressLinkedWithPatient && {
      addressStreet1: patientGuardians[guardian].addressStreet1,
      addressStreet2: patientGuardians[guardian].addressStreet2,
      addressCity: patientGuardians[guardian].addressCity,
      addressState: patientGuardians[guardian].addressState,
      addressZipCode: patientGuardians[guardian].addressZipCode,
    }),
  };

  return policyholderInfo;
};
